import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { ITestableComponentProps } from "@formant/core/model/ITestableComponentProps";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";
import { animate } from "@formant/ui/utils/animate";

import { IJumpSuggestion } from "../../../store/JumpStore";
import { jumpStore, routerStore } from "../../../stores";
import { WindowEvent } from "../../common/WindowEvent";
import { Suggestion } from "./Suggestion";
import * as styles from "./Suggestions.scss";

export interface ISuggestionsProps extends ITestableComponentProps {
    query: string;
    onClose: () => void;
}

@observer
export class Suggestions extends Component<ISuggestionsProps> {
    @observable
    private selected?: number;
    private matches: IJumpSuggestion[] = [];

    public render() {
        const { query, onClose } = this.props;
        const { selected } = this;

        const matches = jumpStore.suggestions(query);
        this.matches = matches || this.matches;
        const loading = matches === undefined;

        if (!loading && this.matches.length === 0) {
            return <div className={styles.noResults}>No results found</div>;
        }

        const suggestionItems = this.matches.map((suggestion, i) => (
            <Suggestion
                key={i}
                index={i}
                selected={i === selected}
                suggestion={suggestion}
                onClicked={onClose}
                onSelect={this.onSelect}
                data-testid={`search-suggestion-${suggestion.title}`}
            />
        ));

        return (
            <div
                className={classnames(
                    styles.suggestions,
                    loading && styles.loading,
                )}
            >
                <WindowEvent type="keydown" onEvent={this.onKeyDown} />
                <LoadingIndicator visible={loading} />
                {suggestionItems}
            </div>
        );
    }

    private onSelect = (index: number) => {
        this.selected = index;
    };

    private next() {
        this.selected =
            ((this.selected !== undefined ? this.selected : -1) + 1) %
            this.matches.length;
    }

    private previous() {
        const count = this.matches.length;
        this.selected = ((this.selected || 0 + count) - 1) % count;
    }

    private async go() {
        const { onClose } = this.props;
        const { selected } = this;
        if (selected !== undefined) {
            const suggestion = this.matches[selected];
            const { to } = suggestion;
            jumpStore.saveRecent(suggestion);
            onClose();
            await animate();
            await routerStore.navigate(to);
        }
    }

    private onKeyDown = async (event: KeyboardEvent) => {
        const { key } = event;
        if (this.matches.length === 0) {
            return;
        }
        if (key === "ArrowDown") {
            event.preventDefault();
            this.next();
        } else if (key === "ArrowUp") {
            event.preventDefault();
            this.previous();
        } else if (key === "Enter" || key === "Tab") {
            event.preventDefault();
            await this.go();
        } else {
            this.selected = undefined;
        }
    };
}
